import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface Timezone {
  zoneName:      string;  // IANA name e.g. "Asia/Kolkata"
  gmtOffset:     number;  // raw offset in seconds
  gmtOffsetName: string;  // e.g. "UTC+05:30"
  abbreviation:  string;  // e.g. "IST"
  tzName:        string;  // e.g. "India Standard Time"
}

export interface Country {
  _id:       string;  // Google place_id
  cscId:     number;
  name:      string;
  iso2:      string;
  iso3:      string;
  emoji:     string;
  timezones: Timezone[];
  status:    string;
}

export interface State {
  _id:  string;  // Google place_id
  name: string;
}

export interface City {
  _id:  string;  // Google place_id
  name: string;
  lat:  number;
  lng:  number;
}

export interface PlaceSuggestion {
  placeId: string;
  name:    string;
}

export interface LocationState {
  country:        Country | null;
  state:          State   | null;
  city:           City    | null;
  timezone:       string;
  timezoneAbbr:   string;
  timezoneOffset: string;
}

const DEFAULT: LocationState = {
  country: null, state: null, city: null,
  timezone: '', timezoneAbbr: '', timezoneOffset: '',
};

@Injectable({ providedIn: 'root' })
export class LocationService {
  private http = inject(HttpClient);
  private readonly KEY         = 'vr_location';
  private readonly PLACES_BASE = 'https://places.googleapis.com/v1/places';
  private readonly TZ_BASE     = 'https://maps.googleapis.com/maps/api/timezone/json';

  private get apiKey(): string { return environment.googleMapsApiKey; }
  private get placeHeaders() {
    return { 'X-Goog-Api-Key': this.apiKey };
  }

  private _loc$ = new BehaviorSubject<LocationState>(this.load());

  /** Subscribe to get live location / timezone updates anywhere in the app. */
  location$: Observable<LocationState> = this._loc$.asObservable();
  get snapshot(): LocationState { return this._loc$.value; }
  get timezone(): string        { return this._loc$.value.timezone; }

  setLocation(state: LocationState): void {
    this._loc$.next(state);
    localStorage.setItem(this.KEY, JSON.stringify(state));
  }

  // ── Country autocomplete (Google Places API - New) ─────────────────────

  searchCountries(input: string): Observable<PlaceSuggestion[]> {
    return this.http
      .post<any>(
        `${this.PLACES_BASE}:autocomplete`,
        { input, includedPrimaryTypes: ['country'], languageCode: 'en' },
        { headers: this.placeHeaders }
      )
      .pipe(map(res => this.mapSuggestions(res)));
  }

  // ── State autocomplete (Google Places API - New) ───────────────────────

  searchStates(input: string, countryIso2: string): Observable<PlaceSuggestion[]> {
    const body: Record<string, any> = {
      input,
      includedPrimaryTypes: ['administrative_area_level_1'],
      languageCode: 'en',
    };
    if (countryIso2) body['includedRegionCodes'] = [countryIso2.toLowerCase()];
    return this.http
      .post<any>(`${this.PLACES_BASE}:autocomplete`, body, { headers: this.placeHeaders })
      .pipe(map(res => this.mapSuggestions(res)));
  }

  // ── City autocomplete (Google Places API - New) ────────────────────────

  searchCities(input: string, countryIso2: string): Observable<PlaceSuggestion[]> {
    const body: Record<string, any> = {
      input,
      includedPrimaryTypes: ['locality'],
      languageCode: 'en',
    };
    if (countryIso2) body['includedRegionCodes'] = [countryIso2.toLowerCase()];
    return this.http
      .post<any>(`${this.PLACES_BASE}:autocomplete`, body, { headers: this.placeHeaders })
      .pipe(map(res => this.mapSuggestions(res)));
  }

  // ── Place Details: country (name + iso2 + lat/lng) ────────────────────

  resolveCountry(placeId: string): Observable<{ name: string; iso2: string; lat: number; lng: number }> {
    return this.http
      .get<any>(`${this.PLACES_BASE}/${placeId}`, {
        headers: {
          ...this.placeHeaders,
          'X-Goog-FieldMask': 'displayName,addressComponents,location',
        },
      })
      .pipe(
        map(res => {
          const countryComp = (res.addressComponents ?? []).find(
            (c: any) => (c.types ?? []).includes('country')
          );
          return {
            name: res.displayName?.text ?? '',
            iso2: countryComp?.shortText ?? '',
            lat:  res.location?.latitude  ?? 0,
            lng:  res.location?.longitude ?? 0,
          };
        })
      );
  }

  // ── Place Details: city lat/lng ────────────────────────────────────────

  resolvePlace(placeId: string): Observable<{ name: string; lat: number; lng: number }> {
    return this.http
      .get<any>(`${this.PLACES_BASE}/${placeId}`, {
        headers: {
          ...this.placeHeaders,
          'X-Goog-FieldMask': 'displayName,location',
        },
      })
      .pipe(
        map(res => ({
          name: res.displayName?.text ?? '',
          lat:  res.location?.latitude  ?? 0,
          lng:  res.location?.longitude ?? 0,
        }))
      );
  }

  // ── Timezone from lat/lng (Google Time Zone API) ───────────────────────

  fetchTimezone(lat: number, lng: number): Observable<Timezone> {
    const timestamp = Math.floor(Date.now() / 1000);
    return this.http
      .get<any>(`${this.TZ_BASE}?location=${lat},${lng}&timestamp=${timestamp}&key=${this.apiKey}`)
      .pipe(
        map(res => {
          const raw   = res.rawOffset ?? 0;
          const dst   = res.dstOffset ?? 0;
          const total = raw + dst;
          const sign  = total >= 0 ? '+' : '-';
          const abs   = Math.abs(total);
          const hh    = String(Math.floor(abs / 3600)).padStart(2, '0');
          const mm    = String(Math.floor((abs % 3600) / 60)).padStart(2, '0');
          return {
            zoneName:      res.timeZoneId   ?? '',
            gmtOffset:     raw,
            gmtOffsetName: `UTC${sign}${hh}:${mm}`,
            abbreviation:  res.timeZoneId   ?? '',
            tzName:        res.timeZoneName ?? '',
          } as Timezone;
        })
      );
  }

  // ── Helpers ───────────────────────────────────────────────────────────

  flagEmoji(iso2: string): string {
    if (!iso2 || iso2.length < 2) return '🌐';
    return [...iso2.toUpperCase().slice(0, 2)]
      .map(c => String.fromCodePoint(0x1F1E0 + c.charCodeAt(0) - 65))
      .join('');
  }

  private mapSuggestions(res: any): PlaceSuggestion[] {
    return (res.suggestions ?? []).map((s: any) => {
      const p = s.placePrediction ?? {};
      return {
        placeId: p.placeId ?? '',
        name:    p.structuredFormat?.mainText?.text ?? p.text?.text ?? '',
      } as PlaceSuggestion;
    }).filter((s: PlaceSuggestion) => s.placeId && s.name);
  }

  private load(): LocationState {
    try {
      const raw = localStorage.getItem(this.KEY);
      return raw ? JSON.parse(raw) : DEFAULT;
    } catch { return DEFAULT; }
  }
}
